import {bootstrapCameraKit, createMediaStreamSource, Transform2D} from "@snap/camera-kit";

class Camera {
    static FRONT_CAMERA = 'user';
    static BACK_CAMERA = 'environment';
    // Steady-state render ratio. Full DPR on a 3x phone is ~3MP/frame, which
    // throttles the GPU, so the live view renders at 2x and is CSS-scaled up.
    static MAX_PIXEL_RATIO = 2;
    // Photos may briefly render at a higher ratio than the live view (see
    // capturePhoto). Lower this to 2 to disable the boost entirely.
    static CAPTURE_PIXEL_RATIO = 3;
    // Camera input resolution. The render size is deliberately kept at or below
    // this: Camera Kit performs best when render size tracks input resolution.
    static IDEAL_CAPTURE_HEIGHT = 1080;
    // How long a muted track is given to come back before a rebuild.
    static UNMUTE_GRACE_MS = 1500;
    // Playback nudges after a resume before handing over to the watchdog.
    static RESUME_RENDER_ATTEMPTS = 3;
    // A rebuild is disruptive, so never repeat one within this window.
    static REBUILD_COOLDOWN_MS = 30000;
    static notSuitableCameras = [
        'wide', 'dual', 'ultra', 'triple', 'telephoto',
        'ширококутна', 'здвоєна', 'надширококутна', 'потроєна', 'телефотокамера'
    ];

    lensIndex = 1;
    lenses = [];
    cameraFacing = Camera.BACK_CAMERA;
    cameras = [];
    frontCamera = null;
    backCamera = null;
    isReady = false;
    currentStream = null;
    isRecovering = false;
    onStreamLost = null;
    stalledSamples = 0;
    hasRendered = false;
    lastRebuildAt = 0;
    sampleContext = null;
    isApplyingLens = false;
    streamSource = null;
    renderSize = null;

    constructor(showDebug) {
        this.showDebug = showDebug;
        this.video = document.createElement('video');
        this.video.setAttribute('muted', '');
        console.log(this.video);
    }

    initialize = async (config) => {
        const {
            apiToken = '',
            lensGroupId = '',
            canvasRef = null,
            cameraFacing = Camera.BACK_CAMERA,
            lensIndex = 0,
        } = config;

        this.canvasRef = canvasRef;
        this.cameraFacing = cameraFacing;
        this.lensIndex = lensIndex;

        // cameras initialization
        await this.requestGyroscopePermission();
        await this.requestCameraPermission();
        await this.prepareCameraList();
        this.printInfo();

        // cameraKit initialization
        const cameraKit = await bootstrapCameraKit({ apiToken });
        this.session = await cameraKit.createSession({
            liveRenderTarget: canvasRef
        });
        const {lenses} = await cameraKit.lensRepository.loadLensGroups([ lensGroupId ]);
        this.lenses = lenses;

        // cameraKit starting
        await this.startCameraKit();

        // apply lens
        await this.applyLens(lensIndex);

        return this;
    }

    startCameraKit = async () => {
        this.isReady = false;
        const mediaStream = await this.getMediaStream();
        this.currentStream = mediaStream;
        this.watchStreamHealth(mediaStream);
        const mediaStreamSourceOptions = {
            transform: this.cameraFacing === Camera.FRONT_CAMERA ? Transform2D.MirrorX : Transform2D.Identity,
            fpsLimit: 60,
            cameraType: this.cameraFacing,
        };
        const streamSource = createMediaStreamSource(mediaStream, mediaStreamSourceOptions);
        this.streamSource = streamSource;
        // Camera Kit must not be handed a source that has not produced a frame
        // yet: it starts the lens (and its world tracking) against an empty
        // texture, which shows as a black screen and mis-seeded tracking. This
        // replaces a fixed 1s sleep that was too short on a cold camera.
        await this.waitForFirstFrame(mediaStream);
        await this.session.setSource(streamSource);
        
        // Set canvas dimensions to match screen size. The pixel ratio is capped
        // because a 3x phone would otherwise render ~3MP per frame at 60fps,
        // which throttles the GPU and eventually freezes the feed. The camera
        // itself only delivers 720p, so the extra pixels were upscaling anyway.
        const pixelRatio = Math.min(window.devicePixelRatio || 1, Camera.MAX_PIXEL_RATIO);
        const screenWidth = Math.round(window.innerWidth * pixelRatio);
        const screenHeight = Math.round(window.innerHeight * pixelRatio);
        
        try {
            await this.setRenderSize(screenWidth, screenHeight);
        } catch (e) {
            console.log(e.message);
        }
        this.isReady = true;
    }

    changeCameraFacing = async (facing) => {
        if (!this.isReady) return;

        if (this.currentStream) {
            this.closeStream(this.currentStream);
            this.currentStream = null;
        }

        this.cameraFacing = facing;
        await this.startCameraKit();
    }

    applyLens = async (index) => {
        if (!this.isReady) return;
        this.isApplyingLens = true;
        try {
            this.lensIndex = index;
            const lens = this.lenses[index];
            if (lens) await this.session.applyLens(lens);
            await this.session.play('live');
        } finally {
            this.isApplyingLens = false;
        }
    }

    pause = async () => {
        if (!this.session) return;
        await this.session.pause();
    }

    resume = async () => {
        if (!this.session) return;
        await this.session.play('live');
    }

    // Lenses render two outputs: 'live' (what the user sees, including any
    // on-screen lens UI) and 'capture' (what is meant to be shared). A lens that
    // opts in leaves its UI out of the capture target, so photos/videos taken
    // from it come out clean. Falls back to the live canvas when the session
    // has no capture output.
    // drawOverlay, when given, is called as (ctx, width, height) so the app can
    // paint its own DOM chrome (the Miele logo) into the saved image - those
    // elements sit above the canvas and are not part of the lens output.
    capturePhoto = async (contentType = 'image/png', drawOverlay = null) => {
        const captureCanvas = this.session?.output?.capture;
        if (!captureCanvas) return this.captureFromLive(contentType, drawOverlay);

        const wasPlaying = this.session.playing?.capture;
        try {
            if (!wasPlaying) await this.session.play('capture');
            await this.waitForRenderedFrames();
            // Freshly started capture targets sit at the 300x150 canvas default
            // until the render thread catches up.
            if (this.renderSize) {
                await this.waitForCanvasSize(captureCanvas, this.renderSize.width, this.renderSize.height);
            }

            if (!captureCanvas.width || !captureCanvas.height) {
                this.showDebug && this.showDebug('Capture target empty, using live');
                return this.captureFromLive(contentType, drawOverlay);
            }
            const blob = await this.withCaptureResolution(captureCanvas, () => new Promise((resolve, reject) => {
                captureCanvas.toBlob(
                    blob => blob ? resolve(blob) : reject(new Error('Capture produced no data')),
                    contentType
                );
            }));
            return drawOverlay ? this.composeOverlay(blob, drawOverlay, contentType) : blob;
        } catch (error) {
            console.error('Capture from capture target failed', error);
            return this.captureFromLive(contentType, drawOverlay);
        } finally {
            // Rendering both targets costs roughly double, so stop again unless
            // something else (a recording) asked for it.
            if (!wasPlaying) await this.stopCaptureRendering();
        }
    }

    // Video recording reads the same clean target; the caller must call
    // stopCaptureRendering() once the recording ends.
    createCaptureStream = async (fps = 30) => {
        const captureCanvas = this.session?.output?.capture;
        if (!captureCanvas) return this.canvasRef?.captureStream(fps) || null;

        if (!this.session.playing?.capture) await this.session.play('capture');
        await this.waitForRenderedFrames();
        if (!captureCanvas.width || !captureCanvas.height) {
            await this.stopCaptureRendering();
            return this.canvasRef?.captureStream(fps) || null;
        }
        return captureCanvas.captureStream(fps);
    }

    stopCaptureRendering = async () => {
        if (!this.session?.playing?.capture) return;
        try {
            await this.session.pause('capture');
        } catch (error) {
            console.error('Failed to pause capture target', error);
        }
    }

    captureFromLive = async (contentType, drawOverlay = null) => {
        if (!this.canvasRef) throw new Error('No render canvas');
        const blob = await new Promise((resolve, reject) => {
            this.canvasRef.toBlob(
                result => result ? resolve(result) : reject(new Error('Capture produced no data')),
                contentType
            );
        });
        return drawOverlay ? this.composeOverlay(blob, drawOverlay, contentType) : blob;
    }

    // Re-encodes the captured frame with the app's own overlay painted on top.
    // Works off the encoded blob rather than the source canvas so it is
    // unaffected by Camera Kit's offscreen ownership of the render targets.
    composeOverlay = async (blob, drawOverlay, contentType) => {
        try {
            const frame = await this.decodeBlob(blob);
            const canvas = document.createElement('canvas');
            canvas.width = frame.width;
            canvas.height = frame.height;
            const context = canvas.getContext('2d');
            context.drawImage(frame, 0, 0);
            await drawOverlay(context, canvas.width, canvas.height);
            if (typeof frame.close === 'function') frame.close();
            return await this.encodeCanvas(canvas, contentType);
        } catch (error) {
            // Never lose the photo over a decoration failure.
            console.error('Failed to draw overlay onto capture', error);
            return blob;
        }
    }

    decodeBlob = async (blob) => {
        if (typeof createImageBitmap === 'function') {
            try {
                return await createImageBitmap(blob);
            } catch (error) {
                console.error('createImageBitmap failed, falling back', error);
            }
        }
        const url = URL.createObjectURL(blob);
        try {
            return await new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.onerror = () => reject(new Error('Could not decode capture'));
                image.src = url;
            });
        } finally {
            URL.revokeObjectURL(url);
        }
    }

    encodeCanvas = (canvas, contentType) => new Promise((resolve, reject) => {
        canvas.toBlob(
            blob => blob ? resolve(blob) : reject(new Error('Capture produced no data')),
            contentType
        );
    });

    setRenderSize = async (width, height) => {
        if (!this.streamSource) return;
        // Camera Kit owns both output canvases - it hands them to an
        // OffscreenCanvas, after which assigning canvas.width/height throws
        // ("Cannot resize canvas after call to transferControlToOffscreen").
        // setRenderSize is the only supported way to resize, and it drives the
        // live and capture targets together.
        await this.streamSource.setRenderSize(width, height);
        this.renderSize = { width, height };
    }

    // Resizing is applied asynchronously on the render thread, so wait for the
    // canvas to actually reach the requested size before reading pixels off it.
    waitForCanvasSize = async (canvas, width, height, attempts = 60) => {
        for (let i = 0; i < attempts; i++) {
            if (canvas.width === width && canvas.height === height) return true;
            await this.waitForRenderedFrames(1);
        }
        return canvas.width === width && canvas.height === height;
    }

    // Live and capture targets always share a render size, so a full-resolution
    // photo means briefly raising the shared size and putting it straight back.
    // Display size is CSS-driven, so this is invisible apart from the extra
    // detail in the saved image.
    withCaptureResolution = async (captureCanvas, task) => {
        const base = this.renderSize;
        const ratio = Math.min(window.devicePixelRatio || 1, Camera.CAPTURE_PIXEL_RATIO);
        const width = Math.round(window.innerWidth * ratio);
        const height = Math.round(window.innerHeight * ratio);

        if (!this.streamSource || !base || (width <= base.width && height <= base.height)) {
            return task();
        }

        try {
            await this.setRenderSize(width, height);
            await this.waitForCanvasSize(captureCanvas, width, height);
            await this.waitForRenderedFrames();
            return await task();
        } catch (error) {
            console.error('Full-resolution capture failed, using live size', error);
            return task();
        } finally {
            try {
                await this.setRenderSize(base.width, base.height);
                await this.waitForCanvasSize(captureCanvas, base.width, base.height);
            } catch (error) {
                console.error('Failed to restore render size', error);
            }
        }
    }

    // The capture target needs a few frames before it holds a complete image.
    waitForRenderedFrames = (frames = 3) => new Promise(resolve => {
        let seen = 0;
        const tick = () => {
            if (++seen >= frames) return resolve();
            requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    });

    // Mobile browsers suspend the camera while the page is backgrounded. iOS in
    // particular ends the video track outright, so on return the canvas keeps
    // painting the last decoded frame ("frozen video") until the source is
    // rebuilt. These helpers detect that and swap in a fresh MediaStream.
    watchStreamHealth = (mediaStream) => {
        const [videoTrack] = mediaStream.getVideoTracks();
        if (!videoTrack) return;
        videoTrack.addEventListener('ended', () => {
            this.showDebug && this.showDebug('Video track ended');
            if (this.onStreamLost) this.onStreamLost();
        });
    }

    // Resolves once the camera has actually decoded a frame (or the timeout
    // lapses, so a misbehaving device still starts rather than hanging).
    waitForFirstFrame = (mediaStream, timeout = 4000) => {
        const video = this.video;
        if (!video) return Promise.resolve();

        return new Promise(resolve => {
            let settled = false;
            const finish = () => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                // Release this sink; Camera Kit owns the stream from here.
                try { video.srcObject = null; } catch (e) { /* ignore */ }
                resolve();
            };
            const timer = setTimeout(() => {
                this.showDebug && this.showDebug('First frame timed out');
                finish();
            }, timeout);

            try {
                video.srcObject = mediaStream;
                video.muted = true;
                video.playsInline = true;
                video.setAttribute('playsinline', 'playsinline');
                const playing = video.play();
                if (playing && playing.catch) playing.catch(() => { /* autoplay guard */ });

                if (typeof video.requestVideoFrameCallback === 'function') {
                    video.requestVideoFrameCallback(() => finish());
                } else if (video.readyState >= 2) {
                    finish();
                } else {
                    video.addEventListener('loadeddata', finish, { once: true });
                }
            } catch (error) {
                console.error('waitForFirstFrame failed', error);
                finish();
            }
        });
    }

    isStreamAlive = () => {
        const stream = this.currentStream;
        if (!stream || !stream.active) return false;
        const [videoTrack] = stream.getVideoTracks();
        if (!videoTrack) return false;
        return videoTrack.readyState === 'live' && !videoTrack.muted;
    }

    // A healthy-looking track is not proof that anything reaches the canvas:
    // after a long background stint Camera Kit's internal video playback can
    // stay stalled, which paints the same frame forever. Sampling the frame
    // counter is the only way to tell that apart from a working feed.
    // Cheap 16x16 checksum of the live canvas: the ground truth for "is the
    // picture actually moving". Sensor noise means consecutive real frames
    // essentially never match, even pointed at a blank wall.
    sampleCanvas = () => {
        const canvas = this.canvasRef;
        if (!canvas || !canvas.width || !canvas.height) return null;

        if (!this.sampleContext) {
            const scratch = document.createElement('canvas');
            scratch.width = 16;
            scratch.height = 16;
            this.sampleContext = scratch.getContext('2d', { willReadFrequently: true });
        }
        if (!this.sampleContext) return null;

        try {
            this.sampleContext.drawImage(canvas, 0, 0, 16, 16);
            const { data } = this.sampleContext.getImageData(0, 0, 16, 16);
            let checksum = 0;
            for (let i = 0; i < data.length; i += 4) {
                checksum += data[i] + data[i + 1] * 3 + data[i + 2] * 7;
            }
            return checksum;
        } catch (error) {
            // Tainted or offscreen-owned canvas: no signal available.
            return null;
        }
    }

    // Deliberately biased towards "healthy": a rebuild is disruptive, so it
    // takes BOTH the frame counter and the canvas pixels to agree that nothing
    // is happening before the feed is declared stalled.
    isRendering = async (sampleMs = 600) => {
        const metrics = this.session?.metrics;
        const measurement = metrics && typeof metrics.beginMeasurement === 'function'
            ? metrics.beginMeasurement()
            : null;
        const pixelsBefore = this.sampleCanvas();

        try {
            await new Promise(resolve => setTimeout(resolve, sampleMs));

            if (measurement) {
                try {
                    const { lensFrameProcessingN = 0, avgFps = 0 } = measurement.measure() || {};
                    if (lensFrameProcessingN > 0 && avgFps > 0) return true;
                } catch (error) {
                    console.error('Could not read render metrics', error);
                    return true;
                }
            }

            const pixelsAfter = this.sampleCanvas();
            // No pixel signal available - do not guess, assume healthy.
            if (pixelsBefore === null || pixelsAfter === null) return !measurement;
            return pixelsBefore !== pixelsAfter;
        } finally {
            if (measurement && typeof measurement.end === 'function') measurement.end();
        }
    }

    // Frames can also stop without any visibility change - a GPU stall, or
    // another app taking the camera. Two consecutive stalled samples are
    // required so one slow moment (applying a lens, a GC pause) does not cause a
    // needless restart.
    runHealthCheck = async () => {
        if (!this.session || !this.isReady || this.isRecovering || this.isApplyingLens) return;
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

        if (!this.isStreamAlive()) {
            this.stalledSamples = 0;
            await this.recover();
            return;
        }

        if (await this.isRendering()) {
            this.hasRendered = true;
            this.stalledSamples = 0;
            return;
        }

        // Before the feed has rendered even once it is still starting up, and a
        // quiet sample says nothing - counting it would restart a camera that is
        // merely slow to come up. This also keeps a rebuild that fails to
        // produce frames from restarting the camera over and over.
        if (!this.hasRendered) return;

        this.stalledSamples += 1;
        if (this.stalledSamples < 2) return;
        if (Date.now() - this.lastRebuildAt < Camera.REBUILD_COOLDOWN_MS) return;

        this.stalledSamples = 0;
        this.showDebug && this.showDebug('Watchdog: frames stalled, rebuilding feed');
        await this.rebuildStream();
    }

    // play() can reject while the page is still settling after a resume, so it
    // gets a second attempt before anything more drastic happens.
    resumePlayback = async () => {
        try {
            await this.session.play('live');
        } catch (error) {
            try {
                await this.session.play('live');
            } catch (retryError) {
                console.error('Failed to resume playback', retryError);
            }
        }
    }

    rebuildStream = async () => {
        this.hasRendered = false;
        this.stalledSamples = 0;
        this.lastRebuildAt = Date.now();

        const previous = this.currentStream;
        try {
            // The old stream is kept until the new one is in place: if
            // getUserMedia fails there is still something to fall back on.
            await this.startCameraKit();
        } catch (error) {
            console.error('Rebuild failed, keeping the previous stream', error);
            this.showDebug && this.showDebug('Rebuild failed: ' + error.message);
            this.currentStream = previous;
            await this.resumePlayback();
            return false;
        }

        if (previous && previous !== this.currentStream) this.closeStream(previous);
        await this.applyLens(this.lensIndex);
        return this.isStreamAlive();
    }

    // Returns true when the feed is confirmed running again.
    recover = async () => {
        if (!this.session || this.isRecovering) return false;
        this.isRecovering = true;
        try {
            // 1. Cheapest fix first: restart the render loop. This alone fixes
            //    the common case.
            await this.resumePlayback();

            // 2. iOS often leaves the track muted for a moment after a resume,
            //    so wait generously - un-muting is far cheaper and safer than a
            //    rebuild.
            if (!this.isStreamAlive()) {
                await this.waitForUnmute(Camera.UNMUTE_GRACE_MS);
                if (!this.isStreamAlive()) return await this.rebuildStream();
            }

            // 3. The stream is alive. Frames can take a while to flow again
            //    after a real suspend, so nudge playback a few more times and
            //    let the watchdog deal with a stall that actually persists.
            for (let attempt = 0; attempt < Camera.RESUME_RENDER_ATTEMPTS; attempt++) {
                if (await this.isRendering()) return true;
                await this.resumePlayback();
            }

            return true;
        } catch (error) {
            console.error('Camera recovery failed', error);
            this.showDebug && this.showDebug('Recovery failed: ' + error.message);
            return false;
        } finally {
            this.isRecovering = false;
        }
    }

    waitForUnmute = (timeout) => {
        const [videoTrack] = this.currentStream?.getVideoTracks() || [];
        if (!videoTrack || videoTrack.readyState !== 'live' || !videoTrack.muted) {
            return Promise.resolve();
        }
        return new Promise(resolve => {
            const done = () => {
                videoTrack.removeEventListener('unmute', done);
                clearTimeout(timer);
                resolve();
            };
            const timer = setTimeout(done, timeout);
            videoTrack.addEventListener('unmute', done);
        });
    }

    // Must be called from a user gesture: iOS only shows the motion
    // permission prompt inside a tap's user-activation window.
    requestGyroscopePermission = async () => {
        const requests = [];
        try {
            // Started together, not awaited in sequence: iOS only honours these
            // inside the tap's activation window and awaiting the first prompt
            // consumes it.
            if (typeof DeviceOrientationEvent !== 'undefined'
                && typeof DeviceOrientationEvent.requestPermission === 'function') {
                requests.push(DeviceOrientationEvent.requestPermission());
            }
            if (typeof DeviceMotionEvent !== 'undefined'
                && typeof DeviceMotionEvent.requestPermission === 'function') {
                requests.push(DeviceMotionEvent.requestPermission());
            }

            if (!requests.length) {
                // Platforms without the permission gate expose motion directly.
                this.motionGranted = true;
                return true;
            }

            const results = await Promise.allSettled(requests);
            this.motionGranted = results.some(r => r.status === 'fulfilled' && r.value === 'granted');
            if (!this.motionGranted) {
                // World tracking degrades to visual-only here: the model will
                // drift and rotate off-centre.
                console.warn('Motion permission not granted', results);
                this.showDebug && this.showDebug('Motion sensors denied - AR tracking will drift');
            }
            return this.motionGranted;
        } catch (error) {
            this.motionGranted = false;
            console.error(error);
            return false;
        }
    }

    requestCameraPermission = async () => {
        const constraints = { video: true, audio: false };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.closeStream(stream);
    }

    closeStream(stream) {
        if (stream) {
            const tracks = stream.getTracks();
            for (let i = 0; i < tracks.length; i++) {
                const track = tracks[i];
                track.stop();
            }
        }
    }

    async prepareCameraList() {
        let cams = await navigator.mediaDevices.enumerateDevices();
        cams = cams.filter(cam => cam.kind === 'videoinput');
        for (const cam of cams) {
            try {
                const camera = {
                    label: cam.label,
                    deviceId: cam.deviceId,
                };
                const video = document.createElement('video');
                video.setAttribute("playsinline", "playsinline");
                video.muted = true;
                const constraints = {
                    audio: false,
                    video: {
                        height: { ideal: 3024 },
                        frameRate: { ideal: 60 },
                        deviceId: cam.deviceId,
                    },
                };
                const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
                video.srcObject = mediaStream;
                const [videoTrack] = mediaStream.getVideoTracks();
                const capabilities = videoTrack.getCapabilities();
                camera.facingMode = capabilities.facingMode || [];
                camera.height = capabilities.height?.max || 0;
                camera.width = capabilities.width?.max || 0;
                this.cameras.push(camera);
                videoTrack.stop();
            } catch (error) {
                console.error(error);
            }
        }

        console.log(`Found ${this.cameras.length} cameras`, this.cameras);
        if (!this.frontCamera && this.cameras.length > 0) {
            this.frontCamera = this.cameras.filter(camera => {
                return (camera.facingMode || []).includes('user');
            })[0] || this.cameras[0];
        }
        if (!this.backCamera && this.cameras.length > 0) {
            this.backCamera = this.cameras.filter(camera => {
                return (camera.facingMode || []).includes('environment')
                    && !Camera.notSuitableCameras.some(word => camera.label.toLowerCase().includes(word));
            })[0] || this.cameras.at(-1);
        }

        console.log({
            frontCamera: this.frontCamera,
            backCamera: this.backCamera,
        });
    }

    async getMediaStream() {
        const videoConstraints = {
            height: { ideal: Camera.IDEAL_CAPTURE_HEIGHT },
            frameRate: { ideal: 60 },
            deviceId: this.cameraFacing === Camera.FRONT_CAMERA
                ? this.frontCamera?.deviceId
                : this.backCamera?.deviceId,
        };

        const mediaConstraints = {
            video: videoConstraints,
            audio: false
        };

        return await navigator.mediaDevices.getUserMedia(mediaConstraints);
    }

    printInfo() {
        if (!this.showDebug) return;
        console.log('front cameras', this.frontCamera);
        console.log('back cameras', this.backCamera);
        if (this.frontCamera) {
            this.showDebug(`Front: ${this.frontCamera.label} (${this.frontCamera.facingMode})`);
        }
        if (this.backCamera) {
            this.showDebug(`Back: ${this.backCamera.label} (${this.backCamera.facingMode})`);
        }
    }
}

export default Camera;
